/**
 * DocumentTypeFormModal — F20 T20.17 (Settings → Typy dokumentów).
 * Company scoping — F20 T20D (Etap C): pole `companyId` (Model B).
 *
 * Modal create/edit/copy dla typu dokumentu.
 * - `mode='create'` → wszystkie pola edytowalne, w tym firma (wymagana)
 * - `mode='edit'`   → kod edytowalny (backend pozwala na zmianę kodu); firma
 *                     NIEedytowalna (backend PUT nie ma pola companyId — firma
 *                     jest ustawiana raz przy tworzeniu i jest immutable)
 * - `mode='copy'`   → kopia lokalna typu globalnego (Etap 4 „Globalne typy
 *                     dokumentów"). Pola prefilowane z wiersza globalnego,
 *                     firma STAŁA (`row.companyId` — Model B, brak pickera,
 *                     jak w mode='edit'). Submit woła COPY endpoint (rodzic
 *                     przekazuje inny handler w `onSubmit`, modal tego nie wie).
 *
 * Walidacja:
 * - code: non-empty, max 30 znaków
 * - name: non-empty, max 100 znaków
 * - companyId: wymagane w mode='create' (mode='edit' nie waliduje — pole tylko podgląd)
 * - description: opcjonalny, max 500 znaków
 * - numberingFormat: opcjonalny, max 50 znaków
 * - sortOrder: opcjonalny, liczba całkowita
 * - isActive: boolean, domyślnie true
 * - numberingBaseField: opcjonalne pole daty (default 'document_date')
 * - numberingReset: polityka resetu licznika (default 'yearly')
 *
 * Miękkie ostrzeżenie o przesłonięciu (Etap 4, `globalCodes` prop): gdy wpisany kod
 * pokrywa się (case-insensitive) z aktywnym typem globalnym, pod polem kodu pojawia się
 * banner informacyjny — NIE blokuje submitu i NIE jest częścią `validate()`. Wyciszone
 * w mode='copy' (przesłonięcie tam zamierzone, ma własną podpowiedź `companyFixedHint`).
 *
 * Layout formularza (T1.8, `layouts`/`layoutsUnavailable` props): pole obecne we
 * wszystkich trybach, bez walidacji klienckiej (soft-FK walidowany przez backend przy
 * zapisie — `validate-form-layout.ts`, kody błędów mapowane na toasty w rodzicu). Gdy
 * `layoutsUnavailable=true` (fetch listy layoutów zawiódł, 403/404) — pole zostaje
 * widoczne, ale disabled + neutralna podpowiedź (ten sam idiom co `companyHint` przy
 * polu firmy), zamiast crashować albo chować pole.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type DocumentClass, normalizeNumberingBaseSlug } from '@saas/shared';

import { Button } from '@/components/Button';
import { NumberingMaskEditor } from '@/components/documents/NumberingMaskEditor';
import type { NumberingBaseFieldOption } from '@/components/documents/useNumberingBaseFieldOptions';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { CompanyPicker, type CompanyPickerOption } from '@/components/ui/CompanyPicker';
import { numberingMaskError } from '@/lib/document-number-preview';

export interface DocumentTypeFormValues {
  code: string;
  name: string;
  numberingFormat: string;
  sortOrder: string;
  isActive: boolean;
  /** T20D.6 — domyślny obieg po utworzeniu dokumentu; '' = brak. */
  defaultProcessId: string;
  /** Pole daty używane jako baza okresu numeracji. */
  numberingBaseField: string;
  /** Polityka resetu licznika numeracji. */
  numberingReset: 'continuous' | 'yearly' | 'monthly';
  /**
   * Firma, do której należy typ dokumentu. Wymagane w mode='create'; w mode='edit'
   * pole jest tylko podglądem (backend PUT nie przyjmuje companyId — immutable po utworzeniu).
   */
  companyId: string | null;
  /** Opis typu dokumentu — opcjonalny, akceptowany przez backend w POST i PUT. */
  description: string;
  /**
   * Layout formularza kartoteki tej klasy dokumentu (T1.8) — soft FK do
   * `system_module_layouts`, walidowany przez backend (`validate-form-layout.ts`).
   * `null` = brak wskazania, formularz używa domyślnej kaskady layoutów kartoteki.
   * W mode='copy' startuje ZAWSZE puste (typ globalny nie zna layoutów tenanta).
   */
  formLayoutId: string | null;
}

/** Opcja pickera domyślnego procesu (opublikowane procesy celujące w dokumenty). */
export interface ProcessOption {
  id: string;
  name: string;
}

/** Opcja pickera layoutu formularza (T1.8) — lista layoutów FORM kartoteki danej klasy. */
export interface FormLayoutOption {
  id: string;
  name: string;
  isDefault: boolean;
  /**
   * Marker pochodzenia layoutu: NOT NULL = layout centralny/producenta (ITE),
   * NULL = nie-centralny. Używany do ikonki pochodzenia w oknie „Przypisz layout".
   */
  sourceViewSlug: string | null;
}

interface Props {
  open: boolean;
  mode: 'create' | 'edit' | 'copy';
  /** Klasa dokumentu aktywnej zakładki — używana do normalizacji zapisanego pola daty numeracji. */
  documentClass: DocumentClass;
  /**
   * Opcje pola daty numeracji budowane w rodzicu (`useNumberingBaseFieldOptions`,
   * `includeExt=true` — core klasy + pola dodatkowe tenanta). WYMAGANE — przekazywane
   * bezpośrednio do `NumberingMaskEditor`, który też wymaga tej listy.
   */
  baseFieldOptions: NumberingBaseFieldOption[];
  initialValues: DocumentTypeFormValues;
  saving?: boolean;
  /** Lista opublikowanych procesów do pickera; podawana z rodzica (T20D.6). */
  processes?: ProcessOption[];
  /** Lista firm tenanta do pickera (mode='create') / etykiety (mode='edit'|'copy'); z useCompanies(). */
  companies?: CompanyPickerOption[];
  /**
   * Kody aktywnych typów globalnych (opublikowanych, niewycofanych, wybieralnych) dla
   * wybranej klasy — używane do miękkiego ostrzeżenia o przesłonięciu typu systemowego
   * (Etap 4). Porównanie case-insensitive. Nie renderowane w mode='copy' (przesłonięcie
   * jest tam zamierzone i już opisane własną podpowiedzią).
   */
  globalCodes?: string[];
  /**
   * Layouty formularza dostępne dla klasy dokumentu tego typu (T1.8) — do pickera
   * „Layout formularza". Podawane z rodzica (React Query, keyowane klasą dokumentu).
   */
  layouts?: FormLayoutOption[];
  /**
   * True gdy pobranie listy layoutów zawiodło (403/404) — pole pozostaje widoczne,
   * ale wyłączone (disabled) z neutralną podpowiedzią zamiast crashować albo chować pole.
   */
  layoutsUnavailable?: boolean;
  onSubmit: (values: DocumentTypeFormValues) => void;
  onClose: () => void;
}

export function DocumentTypeFormModal({
  open,
  mode,
  documentClass,
  baseFieldOptions,
  initialValues,
  saving,
  processes = [],
  companies = [],
  globalCodes = [],
  layouts = [],
  layoutsUnavailable = false,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [code, setCode] = useState(initialValues.code);
  const [name, setName] = useState(initialValues.name);
  const [numberingFormat, setNumberingFormat] = useState(initialValues.numberingFormat);
  const [sortOrder, setSortOrder] = useState(initialValues.sortOrder);
  const [isActive, setIsActive] = useState(initialValues.isActive);
  const [defaultProcessId, setDefaultProcessId] = useState(initialValues.defaultProcessId);
  const [numberingBaseField, setNumberingBaseField] = useState(() =>
    normalizeNumberingBaseSlug(documentClass, initialValues.numberingBaseField),
  );
  const [numberingReset, setNumberingReset] = useState<'continuous' | 'yearly' | 'monthly'>(
    initialValues.numberingReset,
  );
  const [companyId, setCompanyId] = useState(initialValues.companyId);
  const [description, setDescription] = useState(initialValues.description);
  const [formLayoutId, setFormLayoutId] = useState(initialValues.formLayoutId);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setCode(initialValues.code);
      setName(initialValues.name);
      setNumberingFormat(initialValues.numberingFormat);
      setSortOrder(initialValues.sortOrder);
      setIsActive(initialValues.isActive);
      setDefaultProcessId(initialValues.defaultProcessId);
      setNumberingBaseField(
        normalizeNumberingBaseSlug(documentClass, initialValues.numberingBaseField),
      );
      setNumberingReset(initialValues.numberingReset);
      setCompanyId(initialValues.companyId);
      setDescription(initialValues.description);
      setFormLayoutId(initialValues.formLayoutId);
      setErrors({});
    }
  }, [open, initialValues, documentClass]);

  // „Zmieniono" dla zabezpieczenia zamknięcia: firma, proces, layout, maska numeracji i
  // aktywność wybiera się kliknięciem (bez zdarzeń wpisywania), więc porównujemy wartości
  // formularza ze stanem startowym.
  const isDirty =
    JSON.stringify([
      code,
      name,
      numberingFormat,
      sortOrder,
      isActive,
      defaultProcessId,
      numberingBaseField,
      numberingReset,
      companyId,
      description,
      formLayoutId,
    ]) !==
    JSON.stringify([
      initialValues.code,
      initialValues.name,
      initialValues.numberingFormat,
      initialValues.sortOrder,
      initialValues.isActive,
      initialValues.defaultProcessId,
      normalizeNumberingBaseSlug(documentClass, initialValues.numberingBaseField),
      initialValues.numberingReset,
      initialValues.companyId,
      initialValues.description,
      initialValues.formLayoutId,
    ]);

  // Etap 4 — miękkie ostrzeżenie (nie błąd walidacji): kod pokrywa się z aktywnym typem
  // globalnym, więc nowy typ lokalny przesłoni go w tej firmie. W mode='copy' przesłonięcie
  // jest zamierzone (kopia lokalna z typu globalnego) i opisane własną podpowiedzią, więc
  // ostrzeżenie jest tam wyciszone.
  const trimmedCode = code.trim().toLowerCase();
  const collidesWithGlobal =
    mode !== 'copy' &&
    trimmedCode !== '' &&
    globalCodes.some((gc) => gc.toLowerCase() === trimmedCode);

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!code.trim()) {
      next.code = t('tenant.documentTypes.form.errors.codeRequired');
    } else if (code.trim().length > 30) {
      next.code = t('tenant.documentTypes.form.errors.codeLength');
    }
    if (!name.trim()) {
      next.name = t('tenant.documentTypes.form.errors.nameRequired');
    } else if (name.trim().length > 100) {
      next.name = t('tenant.documentTypes.form.errors.nameLength');
    }
    // Firma jest wymagana tylko przy tworzeniu — backend PUT nie ma pola companyId,
    // więc w mode='edit'/'copy' pole jest tylko podglądem (stałe) i nie podlega walidacji.
    if (mode === 'create' && !companyId) {
      next.companyId = t('tenant.documentTypes.form.errors.companyRequired');
    }
    if (description.length > 500) {
      next.description = t('tenant.documentTypes.form.errors.descriptionLength');
    }
    if (numberingFormat.length > 50) {
      next.numberingFormat = t('tenant.documentTypes.form.errors.numberingFormatLength');
    } else if (numberingFormat.trim()) {
      const maskErr = numberingMaskError(numberingFormat);
      if (maskErr === 'invalid') {
        next.numberingFormat = t('tenant.documentTypes.form.errors.numberingFormatInvalid');
      } else if (maskErr === 'noCounter') {
        next.numberingFormat = t('tenant.documentTypes.form.errors.numberingFormatNoCounter');
      }
    }
    if (sortOrder !== '' && !/^-?\d+$/.test(sortOrder.trim())) {
      next.sortOrder = t('tenant.documentTypes.form.errors.sortOrderInvalid');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    onSubmit({
      code: code.trim(),
      name: name.trim(),
      numberingFormat: numberingFormat.trim(),
      sortOrder: sortOrder.trim(),
      isActive,
      defaultProcessId,
      numberingBaseField,
      numberingReset,
      // W mode='edit' companyId jest immutable — przekazujemy stan wejściowy (rodzic
      // i tak nie wysyła tego pola w PUT), żeby typ pozostał kompletny.
      companyId,
      description: description.trim(),
      formLayoutId,
    });
  }

  const titleKey =
    mode === 'create'
      ? 'tenant.documentTypes.form.titleCreate'
      : mode === 'copy'
        ? 'tenant.documentTypes.form.titleCopy'
        : 'tenant.documentTypes.form.titleEdit';

  // mode='edit'/'copy' — firma jest stała; renderujemy tylko etykietę (lookup po companyId).
  const companyName = companies.find((c) => c.id === companyId)?.legalName ?? t('common.notSet');
  const companyHint =
    mode === 'edit'
      ? t('tenant.documentTypes.form.companyImmutableHint')
      : mode === 'copy'
        ? t('tenant.documentTypes.form.companyFixedHint')
        : undefined;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(titleKey)}
      // Szerokie okno z dwiema kolumnami pól + „rozszerz": dawne `sm` (448 px) robiło z formularza
      // wąską, wysoką kolumnę ze scrollem, której nie dało się powiększyć.
      size="xl"
      expandable
      testId="document-type-form-modal"
      guardUnsaved
      dirty={isDirty}
      // W trakcie zapisu tło i Esc nie zamykają okna („Anuluj" jest wyłączone; X w nagłówku zostaje).
      closeOnBackdrop={!saving}
      closeOnEsc={!saving}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
            data-testid="document-type-form-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={saving}
            data-testid="document-type-form-submit"
          >
            {mode === 'create'
              ? t('common.create')
              : mode === 'copy'
                ? t('tenant.documentTypes.form.submitCopy')
                : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2">
        <FormField label={t('tenant.documentTypes.form.code')} error={errors.code}>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t('tenant.documentTypes.form.codePlaceholder')}
            data-testid="document-type-form-code"
          />
        </FormField>
        {collidesWithGlobal && (
          <div
            className="rounded-lg border border-warning-200 bg-warning-50 px-3 py-2 text-sm text-warning-900 md:col-span-2"
            data-testid="document-type-form-collision-warning"
          >
            {t('tenant.documentTypes.form.collisionWarning', { code: code.trim() })}
          </div>
        )}
        <FormField label={t('tenant.documentTypes.form.name')} error={errors.name}>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('tenant.documentTypes.form.namePlaceholder')}
            data-testid="document-type-form-name"
          />
        </FormField>
        {mode === 'create' ? (
          <FormField
            label={t('tenant.documentTypes.form.company')}
            error={errors.companyId}
            required
          >
            <CompanyPicker
              value={companyId}
              onChange={setCompanyId}
              companies={companies}
              required
              testId="document-type-form-company"
            />
          </FormField>
        ) : (
          <FormField label={t('tenant.documentTypes.form.company')} hint={companyHint}>
            <Input
              value={companyName}
              disabled
              readOnly
              data-testid="document-type-form-company-readonly"
            />
          </FormField>
        )}
        <FormField
          label={t('tenant.documentTypes.form.description')}
          error={errors.description}
          className="md:col-span-2"
        >
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('tenant.documentTypes.form.descriptionPlaceholder')}
            rows={2}
            className="w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary"
            data-testid="document-type-form-description"
          />
        </FormField>
        <div className="md:col-span-2">
          <NumberingMaskEditor
            code={code}
            numberingFormat={numberingFormat}
            numberingBaseField={numberingBaseField}
            baseFieldOptions={baseFieldOptions}
            numberingReset={numberingReset}
            onFormatChange={setNumberingFormat}
            onBaseFieldChange={setNumberingBaseField}
            onResetChange={setNumberingReset}
            formatError={errors.numberingFormat}
          />
        </div>
        <FormField label={t('tenant.documentTypes.form.sortOrder')} error={errors.sortOrder}>
          <Input
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            placeholder={t('tenant.documentTypes.form.sortOrderPlaceholder')}
            data-testid="document-type-form-sort-order"
          />
        </FormField>
        <FormField label={t('tenant.documentTypes.form.defaultProcess')}>
          <Dropdown
            value={defaultProcessId}
            onChange={(v) => setDefaultProcessId(v)}
            data-testid="document-type-form-default-process"
            options={[
              { value: '', label: t('tenant.documentTypes.form.defaultProcessNone') },
              ...processes.map((p) => ({ value: p.id, label: p.name })),
            ]}
          />
        </FormField>
        <FormField
          label={t('tenant.documentTypes.form.formLayout')}
          hint={
            layoutsUnavailable
              ? t('tenant.documentTypes.form.formLayoutUnavailableHint')
              : undefined
          }
        >
          <Dropdown
            value={formLayoutId ?? ''}
            onChange={(v) => setFormLayoutId(v || null)}
            disabled={layoutsUnavailable}
            data-testid="document-type-form-form-layout"
            options={[
              { value: '', label: t('tenant.documentTypes.form.formLayoutNone') },
              ...layouts.map((l) => ({
                value: l.id,
                label: l.isDefault
                  ? `${l.name} ${t('tenant.documentTypes.form.formLayoutDefaultSuffix')}`
                  : l.name,
              })),
              // Orphan indication (post-review fix) — `formLayoutId` wskazuje na layout, którego
              // nie ma w bieżącej liście `layouts` (np. usunięty/przeniesiony). Bez tej opcji
              // select fałszywie pokazałby pustą/domyślną opcję, mimo że stan trzyma realne UUID.
              ...(formLayoutId !== null && !layouts.some((l) => l.id === formLayoutId)
                ? [
                    {
                      value: formLayoutId,
                      label: t('tenant.documentTypes.form.formLayoutMissing'),
                    },
                  ]
                : []),
            ]}
          />
        </FormField>
        <div className="flex items-center gap-2 md:col-span-2">
          <input
            type="checkbox"
            id="document-type-form-is-active"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded border-border-primary"
            data-testid="document-type-form-is-active"
          />
          <label htmlFor="document-type-form-is-active" className="text-sm text-text-primary">
            {t('tenant.documentTypes.form.isActive')}
          </label>
        </div>
      </div>
    </Modal>
  );
}
